import { Link } from 'react-router-dom';
import { ArrowRight, Star, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatPrice } from '../../lib/utils';
import { normCat, CATEGORY_COLORS, CATEGORY_ICONS } from './constants';
import { CountdownTimer } from './CountdownTimer';
import { useRef, useMemo } from 'react';

const OfferCard = ({ product }) => (
    <Link to={`/produit/${product.id}`} className="group flex-shrink-0 w-[260px] sm:w-[280px]" data-testid={`weekly-offer-${product.id}`}>
        <Card className="overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 rounded-2xl h-full">
            <div className="relative aspect-square overflow-hidden bg-gray-50">
                <img src={product.image_url} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                {/* Single elegant promo indicator */}
                <div className="absolute top-0 left-0">
                    <div className="bg-gradient-to-br from-red-500 to-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-br-xl shadow-md">
                        Promo
                    </div>
                </div>
                {product.origin && (
                    <div className="absolute bottom-3 left-3">
                        <span className="bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full">
                            {product.origin}
                        </span>
                    </div>
                )}
            </div>
            <CardContent className="p-4">
                <h3 className="font-semibold text-base text-gray-900 line-clamp-1 group-hover:text-emerald-600 transition-colors">{product.name}</h3>
                {product.description && (
                    <p className="text-gray-500 text-xs mt-1 line-clamp-1">{product.description}</p>
                )}
                <div className="mt-3 flex items-center justify-between">
                    <div>
                        <span className="font-bold text-lg text-emerald-600">{formatPrice(product.price)}</span>
                        <span className="text-gray-400 text-xs ml-1">/ {product.unit}</span>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                        <ShoppingCart className="w-4 h-4 text-emerald-600 group-hover:text-white transition-colors" />
                    </div>
                </div>
            </CardContent>
        </Card>
    </Link>
);

const ScrollableRow = ({ products }) => {
    const scrollRef = useRef(null);

    const scroll = (dir) => {
        if (!scrollRef.current) return;
        const amount = 300;
        scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
    };

    const showArrows = products.length > 4;

    return (
        <div className="relative group/scroll">
            {showArrows && (
                <>
                    <button
                        onClick={() => scroll('left')}
                        className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-gray-50"
                        data-testid="scroll-left"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-gray-50"
                        data-testid="scroll-right"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-700" />
                    </button>
                </>
            )}
            <div
                ref={scrollRef}
                className="flex gap-5 overflow-x-auto scrollbar-hide pb-2 scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {products.map((product) => (
                    <OfferCard key={product.id} product={product} />
                ))}
            </div>
        </div>
    );
};

export const WeeklyOffers = ({ offersByCategory, hiddenCategories, promoEndDate, setPromoExpired }) => {
    const filteredOffers = useMemo(
        () => offersByCategory.filter(co => !hiddenCategories.includes(normCat(co.category_id))),
        [offersByCategory, hiddenCategories]
    );

    return (
    <section className="py-16 md:py-24 bg-[#FAFAF5]" data-testid="weekly-offers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-3">
                    <Star className="w-3 h-3 inline mr-1 fill-amber-500" />
                    Promotions
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
                    Offres de la Semaine
                </h2>
                <p className="text-gray-500 mt-3 max-w-lg mx-auto">
                    Profitez de nos meilleures offres selectionnees pour vous
                </p>
            </div>

            {promoEndDate && (
                <div className="flex justify-center mb-10">
                    <div className="bg-gradient-to-r from-amber-600 via-red-500 to-amber-600 rounded-2xl px-6 py-4 shadow-xl" data-testid="promo-countdown-banner">
                        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
                            <p className="text-white font-bold text-sm md:text-base uppercase tracking-wide">
                                Fin de la promo dans
                            </p>
                            <CountdownTimer endDate={promoEndDate} onExpire={() => setPromoExpired(true)} />
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-12">
                {filteredOffers.map((categoryOffers) => {
                    const nId = normCat(categoryOffers.category_id);
                    return (
                    <div key={categoryOffers.category_id}>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                                style={{ backgroundColor: `${CATEGORY_COLORS[nId] || '#D97706'}20` }}>
                                {CATEGORY_ICONS[nId] || '📦'}
                            </div>
                            <h3 className="font-bold text-xl text-gray-900">{categoryOffers.category_name}</h3>
                            <Badge className="text-white rounded-full" style={{ backgroundColor: CATEGORY_COLORS[nId] || '#D97706' }}>
                                {categoryOffers.count} offre{categoryOffers.count > 1 ? 's' : ''}
                            </Badge>
                            <div className="flex-1 h-px bg-gray-200"></div>
                        </div>

                        <ScrollableRow products={categoryOffers.products} />
                    </div>
                );
                })}
            </div>

            <div className="text-center mt-12">
                <Button asChild size="lg" className="bg-gray-900 hover:bg-gray-800 text-white rounded-full h-12 px-8">
                    <Link to="/catalogue">
                        Voir tout le catalogue
                        <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                </Button>
            </div>
        </div>
    </section>
    );
};
